import { createReducer, createSelector, on } from '@ngrx/store';
import { AppState } from '../store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import {
    CreateOffice,
    CreateOfficeSuccess,
    LoadAllOfficesSuccess,
    LoadOfficesFailure,
    OfficeActionFailure,
    ReloadOfficeFailure,
    ReloadOfficeSuccess,
    UpdateOffice
} from '../actions/office.actions';
import { OfficeDTO } from '@iota/iota-management-portal-v4-client';
import { DefaultOffice } from 'src/app/shared/constants-and-enums/default-objects';

export interface OfficeState extends EntityState<OfficeDTO> {
    error: any;
    loading: boolean;
    actionLoading: boolean;
    loaded: boolean;
    lastCreatedOfficeId: string;
}

function selectOfficeId(a: OfficeDTO): string {
    //In this case this would be optional since primary key is id
    return a.officeId;
}

function sortOfficesByName(a: OfficeDTO, b: OfficeDTO): number {
    return a.name.localeCompare(b.name);
}

export const officeAdapter: EntityAdapter<OfficeDTO> = createEntityAdapter<OfficeDTO>({
    selectId: selectOfficeId,
    sortComparer: sortOfficesByName
});

export const initialState: OfficeState = officeAdapter.getInitialState({
    // additional entity state properties
    error: undefined,
    loading: true,
    actionLoading: false,
    loaded: false,
    lastCreatedOfficeId: undefined
});

export const officeReducer = createReducer(
    initialState,
    /* Office */
    on(LoadAllOfficesSuccess, (state, { offices }) => {
        if (!offices || offices.length == 0) {
            offices = [DefaultOffice()];
        }
        return officeAdapter.setAll(offices, { ...state, loading: false, actionLoading: false, loaded: true });
    }),
    on(LoadOfficesFailure, (state, { error }) => ({ ...state, error, loading: false })),

    on(ReloadOfficeSuccess, (state, { office }) => officeAdapter.upsertOne(office, { ...state, actionLoading: false })),
    on(ReloadOfficeFailure, (state, { error }) => ({ ...state, error, actionLoading: false })),

    on(OfficeActionFailure, (state, { error }) => ({ ...state, error, actionLoading: false })),

    on(UpdateOffice, CreateOffice, (state) => ({ ...state, actionLoading: true })),
    on(CreateOfficeSuccess, (state, { officeId }) => ({ ...state, lastCreatedOfficeId: officeId }))
);

// get the selectors
const { selectAll } = officeAdapter.getSelectors();

export const officeState = (state: AppState) => state.officeState;
export const allOffices = createSelector(officeState, selectAll);
export const officeActionLoading = createSelector(officeState, (state) => state.actionLoading);
export const officesLoading = createSelector(officeState, (state) => state.loading);
export const officesLoaded = createSelector(officeState, (state) => state.loaded);
export const officeById = (officeId: string) => createSelector(allOffices, (allOffices) => allOffices.find((o) => o.officeId === officeId));
export const lastCreatedOfficeId = createSelector(officeState, (state) => state.lastCreatedOfficeId);
